import { useCallback, useContext } from 'react';
import RolesContext from 'contexts/RolesContext';
import { SharedContext } from '../../contextProviders/SplashScreenProviders/ContentDependantProviders/SharedProvider';
import { PortalsContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import portalTypeCheckers from '../../types/portalTypeCheckers';
import PERMISSIONS from '../../permissionDefinitions';

// Note: This should be used after the ContentProvider in the SplashScreen
const useV2HasAccess = () => {
  const { currentPermissions } = useContext(RolesContext);
  const { selectedPortal, availablePortals } = useContext(PortalsContext);
  const { sharedContent } = useContext(SharedContext);

  const isShared = useCallback(
    ({
      type,
      typeId,
    }: {
      type:
        | 'targetsApp'
        | 'entity'
        | 'dashboard'
        | 'report'
        | 'wallboard'
        | 'workspace'
        | 'goal'
        | 'scorecard'
        | 'board';
      typeId: string;
    }) => {
      if (type === 'report') {
        return sharedContent.reports.some((r) => r.id === typeId);
      } else if (type === 'dashboard') {
        return sharedContent.dashboards.some((d) => d.id === typeId);
      } else if (type === 'entity') {
        return true;
      } else if (type === 'scorecard') {
        return sharedContent.scorecards.some((s) => s.id === typeId);
      } else if (type === 'workspace') {
        return sharedContent.workSpaces.some((w) => w.id === typeId);
      }
      return false;
    },
    [sharedContent],
  );

  const portalHasAccess = useCallback(
    ({
      type,
      typeId,
      portal,
    }: {
      type:
        | 'targetsApp'
        | 'entity'
        | 'dashboard'
        | 'report'
        | 'wallboard'
        | 'workspace'
        | 'goal'
        | 'scorecard'
        | 'board';
      typeId: string;
      portal: Portal;
    }) => {
      if (portalTypeCheckers.isAdminPortal(portal)) {
        return true;
      } else if (
        portalTypeCheckers.isExecutivePortal(portal) ||
        portalTypeCheckers.isEngagementPortal(portal)
      ) {
        if (type === 'entity') {
          return true;
        }

        if (type === 'scorecard' && portal.scorecardIds.includes(typeId)) {
          return true;
        }

        if (type === 'report' && portal.reportIds.includes(typeId)) {
          return true;
        }

        if (type === 'dashboard' && portal.dashboardIds.includes(typeId)) {
          return true;
        }

        return false;
      } else {
        return false;
      }
    },
    [],
  );

  const hasPortalAccess = useCallback(
    ({
      type,
      typeId,
    }: {
      type:
        | 'targetsApp'
        | 'entity'
        | 'dashboard'
        | 'report'
        | 'wallboard'
        | 'workspace'
        | 'goal'
        | 'scorecard'
        | 'board';
      typeId: string;
    }) => {
      return availablePortals.some((portal) =>
        portalHasAccess({ portal, type, typeId }),
      );
    },
    [availablePortals, portalHasAccess],
  );

  const currentPortalHasAccess = useCallback(
    ({
      type,
      typeId,
    }: {
      type:
        | 'targetsApp'
        | 'entity'
        | 'dashboard'
        | 'report'
        | 'wallboard'
        | 'workspace'
        | 'goal'
        | 'scorecard'
        | 'board';
      typeId: string;
    }) => {
      if (!selectedPortal) {
        return false;
      }

      return portalHasAccess({
        portal: selectedPortal,
        type,
        typeId,
      });
    },
    [portalHasAccess, selectedPortal],
  );

  const v2HasAccess = useCallback(
    ({
      type,
      typeId,
    }: {
      type:
        | 'targetsApp'
        | 'entity'
        | 'dashboard'
        | 'report'
        | 'wallboard'
        | 'workspace'
        | 'goal'
        | 'scorecard'
        | 'board';
      typeId: string;
    }): boolean => {
      if (selectedPortal) {
        if (type === 'workspace') {
          return (
            currentPermissions.includes(
              PERMISSIONS.CONTENT_ACCESS.VIEW_ADMIN_PORTAL,
            ) ||
            currentPortalHasAccess({ type, typeId }) ||
            isShared({ type, typeId })
          );
        }
        return currentPortalHasAccess({ type, typeId });
      }

      return hasPortalAccess({ type, typeId }) || isShared({ type, typeId });
    },
    [
      currentPermissions,
      currentPortalHasAccess,
      hasPortalAccess,
      isShared,
      selectedPortal,
    ],
  );

  return v2HasAccess;
};

export default useV2HasAccess;
